import { useRef, useState } from "react";
import { Play, Volume2, VolumeX, Maximize2 } from "lucide-react";

const VIDEO_SRC = "/hero.mp4";
const VIDEO_POSTER = "/dhruva.jpg";

/**
 * Dedicated full-bleed video section. Plays muted on autoplay so users
 * can preview without sound, with mute / fullscreen controls revealed
 * on hover. The container letterboxes to fit the video without cropping.
 */
export default function VideoShowcase() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPaused(false); }
    else { v.pause(); setPaused(true); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const goFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <section
      data-testid="video-showcase"
      className="section-pad bg-sand-100"
      aria-label="See the Gurukul method"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-8 md:mb-10">
          <p className="eyebrow">See the method</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-forest leading-tight">
            Your complete transformation <em className="italic text-clay">– at home.</em>
          </h2>
          <p className="mt-5 md:mt-6 font-serif text-2xl sm:text-3xl md:text-4xl text-forest leading-snug font-medium tracking-tight">
            <span className="inline-block">No gym.</span>{" "}
            <span className="inline-block">No equipment.</span>{" "}
            <span className="inline-block italic text-clay">No commute.</span>
          </p>
        </div>

        <div className="group relative rounded-[1.5rem] md:rounded-[2rem] overflow-hidden border border-sand-300 shadow-2xl bg-forest-dark">
          <video
            ref={videoRef}
            src={VIDEO_SRC}
            poster={VIDEO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            controls={false}
            onClick={togglePlay}
            className="block w-full h-auto max-h-[78vh] object-contain bg-forest-dark mx-auto cursor-pointer"
            data-testid="video-element"
          />

          {/* play overlay if paused */}
          {paused && (
            <button
              onClick={togglePlay}
              aria-label="Play video"
              className="absolute inset-0 m-auto h-16 w-16 rounded-full bg-white/95 backdrop-blur-md grid place-items-center shadow-xl hover:scale-105 transition-transform"
            >
              <Play className="h-6 w-6 text-forest fill-forest" />
            </button>
          )}

          {/* mute + fullscreen controls */}
          <div className="absolute bottom-3 right-3 md:bottom-5 md:right-5 flex items-center gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            <button
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="video-mute-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-black/80 transition-colors"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <button
              onClick={goFullscreen}
              aria-label="Fullscreen"
              data-testid="video-fullscreen-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-black/80 transition-colors"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
